/**
 * Icon set — lucide-react, the icon library shared with IPAS and the
 * Self-Service app (which uses lucide-react-native).
 *
 * Was `ifasIcons.tsx`, which was misleading: nothing here is IFAS-specific and
 * the claims navigation uses it too. The name mattered because the IFAS
 * extraction (C4) planned to move this file wholesale — it has to be
 * duplicated into both repositories instead, since both products draw from it.
 *
 * Each export wraps a lucide icon so it accepts the MUI-style props the IFAS
 * screens already pass (`sx`, `fontSize`, `color` as a palette key or CSS
 * colour), letting call sites keep their existing JSX. Lucide draws with
 * `stroke: currentColor`, so CSS `color` flows through naturally.
 */
import { forwardRef } from "react";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import {
  Ambulance,
  Archive,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Banknote,
  BadgeCheck,
  BarChart3,
  BellRing,
  Box as Box3d,
  Boxes,
  Calendar,
  CalendarCheck,
  CalendarDays,
  Car,
  CarFront,
  CheckCheck,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  CircleCheckBig,
  CirclePlay,
  CircleX,
  ClipboardCheck,
  ClipboardList,
  Clock,
  CloudUpload,
  ConciergeBell,
  EllipsisVertical,
  Eye,
  FileCheck,
  FileText,
  Filter,
  HardHat,
  History,
  House,
  Hourglass,
  IdCard,
  Landmark,
  ListChecks,
  MapPin,
  Menu as MenuLucide,
  MessageSquare,
  MessageSquareText,
  NotebookPen,
  Package,
  Pencil,
  PenLine,
  Phone,
  Plus,
  Receipt,
  ReceiptText,
  RefreshCw,
  RotateCw,
  Route,
  Ruler,
  Search as SearchLucide,
  Send,
  Snowflake,
  Store,
  Tags,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Truck,
  User,
  Users,
  Utensils,
  Warehouse,
  Wrench,
  X,
  Flower2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type MuiFontSize = "inherit" | "small" | "medium" | "large";

interface IconProps {
  sx?: SxProps<Theme>;
  fontSize?: MuiFontSize | number;
  color?: string;
  size?: number;
  strokeWidth?: number;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  style?: React.CSSProperties;
}

const FONT_SIZE_PX: Record<string, number | string> = {
  inherit: "1em",
  small: 20,
  medium: 24,
  large: 35,
};

/** Adapts a lucide icon to the MUI icon prop surface used across IFAS. */
function asMuiIcon(Lucide: LucideIcon) {
  return forwardRef<SVGSVGElement, IconProps>(function IfasIcon(
    { sx, fontSize, color, size, strokeWidth = 2, ...rest },
    ref,
  ) {
    const dim = size ?? (typeof fontSize === "number" ? fontSize : FONT_SIZE_PX[fontSize ?? "medium"]);
    return (
      <Box
        component={Lucide}
        ref={ref}
        strokeWidth={strokeWidth}
        sx={{
          width: dim,
          height: dim,
          flexShrink: 0,
          verticalAlign: "middle",
          ...(color ? { color } : null),
          ...sx,
        }}
        {...rest}
      />
    );
  });
}

// Names mirror the MUI icons they replace so import sites read naturally.
export const AccessTimeOutlined = asMuiIcon(Clock);
export const AccountBalanceOutlined = asMuiIcon(Landmark);
export const AddOutlined = asMuiIcon(Plus);
export const AmbulanceOutlined = asMuiIcon(Ambulance);
export const ArchiveOutlined = asMuiIcon(Archive);
export const ArrowBackOutlined = asMuiIcon(ArrowLeft);
export const ArrowForwardOutlined = asMuiIcon(ArrowRight);
export const ArrowOutwardOutlined = asMuiIcon(ArrowUpRight);
export const ArticleOutlined = asMuiIcon(FileText);
export const AssignmentOutlined = asMuiIcon(ClipboardList);
export const AssignmentTurnedInOutlined = asMuiIcon(ClipboardCheck);
export const AutorenewOutlined = asMuiIcon(RefreshCw);
export const BadgeOutlined = asMuiIcon(IdCard);
export const BarChartOutlined = asMuiIcon(BarChart3);
export const BorderColor = asMuiIcon(PenLine);
export const BorderColorOutlined = asMuiIcon(PenLine);
export const CalendarMonthOutlined = asMuiIcon(Calendar);
export const CallOutlined = asMuiIcon(Phone);
export const CheckCircleOutline = asMuiIcon(CircleCheck);
export const CheckCircleOutlineOutlined = asMuiIcon(CircleCheck);
export const CheckCircleOutlineRounded = asMuiIcon(CircleCheck);
export const ChecklistOutlined = asMuiIcon(ListChecks);
export const Close = asMuiIcon(X);
export const CloseOutlined = asMuiIcon(X);
export const CloudUploadOutlined = asMuiIcon(CloudUpload);
export const DeleteOutline = asMuiIcon(Trash2);
export const DescriptionOutlined = asMuiIcon(FileText);
export const DirectionsCarFilledOutlined = asMuiIcon(CarFront);
export const DirectionsCarOutlined = asMuiIcon(Car);
export const Diversity = asMuiIcon(Users);
export const Diversity3Outlined = asMuiIcon(Users);
export const Inventory2Outlined = asMuiIcon(Boxes);
export const DoneAllOutlined = asMuiIcon(CheckCheck);
export const EditNoteOutlined = asMuiIcon(NotebookPen);
export const EditOutlined = asMuiIcon(Pencil);
export const EngineeringOutlined = asMuiIcon(HardHat);
export const EventAvailableOutlined = asMuiIcon(CalendarCheck);
export const EventNoteOutlined = asMuiIcon(CalendarDays);
export const ExpandLess = asMuiIcon(ChevronUp);
export const ExpandMore = asMuiIcon(ChevronDown);
export const FactCheckOutlined = asMuiIcon(FileCheck);
export const FeedbackOutlined = asMuiIcon(MessageSquareText);
export const FilterAltOutlined = asMuiIcon(Filter);
export const HighlightOffRounded = asMuiIcon(CircleX);
export const HistoryOutlined = asMuiIcon(History);
export const HomeOutlined = asMuiIcon(House);
export const Inventory = asMuiIcon(Package);
export const LocalOfferOutlined = asMuiIcon(Tags);
export const LocalShippingOutlined = asMuiIcon(Truck);
export const Menu = asMuiIcon(MenuLucide);
export const MoreVert = asMuiIcon(EllipsisVertical);
export const NotificationsActiveOutlined = asMuiIcon(BellRing);
export const PaidOutlined = asMuiIcon(Banknote);
export const PendingActionsOutlined = asMuiIcon(Hourglass);
export const PersonOutlineOutlined = asMuiIcon(User);
export const PlaceOutlined = asMuiIcon(MapPin);
export const PlayCircleOutlineOutlined = asMuiIcon(CirclePlay);
export const RateReviewOutlined = asMuiIcon(MessageSquare);
export const ReceiptRounded = asMuiIcon(Receipt);
export const RefreshOutlined = asMuiIcon(RotateCw);
export const RequestQuoteOutlined = asMuiIcon(ReceiptText);
export const ReportProblemOutlined = asMuiIcon(TriangleAlert);
export const RestaurantMenuOutlined = asMuiIcon(Utensils);
export const RoomServiceOutlined = asMuiIcon(ConciergeBell);
export const RouteOutlined = asMuiIcon(Route);
export const Search = asMuiIcon(SearchLucide);
export const SendOutlined = asMuiIcon(Send);
export const StorefrontOutlined = asMuiIcon(Store);
export const StraightenOutlined = asMuiIcon(Ruler);
export const TaskAltOutlined = asMuiIcon(CircleCheckBig);
export const TrendingUpOutlined = asMuiIcon(TrendingUp);
export const VerifiedOutlined = asMuiIcon(BadgeCheck);
export const ViewInArOutlined = asMuiIcon(Box3d);
export const VisibilityOutlined = asMuiIcon(Eye);
export const WarehouseOutlined = asMuiIcon(Warehouse);
export const WarningAmberOutlined = asMuiIcon(TriangleAlert);
export const BuildOutlined = asMuiIcon(Wrench);
export const AcUnitOutlined = asMuiIcon(Snowflake);
export const YardOutlined = asMuiIcon(Flower2);
